/**
 * Daily writing reminder (D4 of PRODUCT_PLAN).
 *
 * Local preference (HH:MM time, no push server — privacy):
 *   · Web/PWA: foreground notification while the app is alive (timer).
 *   · Tauri: OS-scheduled notification (web-app adapter).
 */

const FNV_OFFSET_BASIS = 0x81_1c_9d_c5;
const FNV_PRIME = 0x01_00_01_93;
const INT32_MAX = 2_147_483_647;

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/u;

/** Default time for the daily reminder. */
export const DEFAULT_REMINDER_TIME = "20:00";

/** Stable identifier for the daily reminder in the local registry. */
export const DAILY_REMINDER_ID = "journal-daily-reminder";

/** Reminder registered in the OS (Tauri adapter) or in memory (web). */
export interface ScheduledReminder {
  /** OS identifier for the notification. */
  id: string;
  /** ISO instant for which it was scheduled. */
  whenISO?: string | null;
}

/**
 * Stable numeric ID for the Tauri notification plugin (FNV-1a normalized
 * to a positive i32).
 */
export function reminderNumericId(reminderId: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < reminderId.length; i += 1) {
    // FNV-1a: XOR of the next code unit and multiplication by the prime.
    // oxlint-disable-next-line eslint/no-bitwise
    hash ^= reminderId.codePointAt(i) ?? 0;
    hash = Math.imul(hash, FNV_PRIME);
  }
  return ((hash % INT32_MAX) + INT32_MAX) % INT32_MAX;
}

/** Validates "HH:MM" (24 h). */
export function isValidReminderTime(value: string): boolean {
  return TIME_RE.test(value);
}

/**
 * Next reminder trigger: today at the specified time if it has not passed yet,
 * otherwise tomorrow. All in the device's LOCAL time.
 */
export function nextReminderAt(timeHHMM: string, now: Date = new Date()): Date | null {
  const match = TIME_RE.exec(timeHHMM);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const next = new Date(now);
  next.setHours(hours, minutes, 0, 0);
  if (next.getTime() <= now.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

/** Milliseconds until the next trigger (for foreground timers). */
export function msUntilReminder(timeHHMM: string, now: Date = new Date()): number | null {
  const next = nextReminderAt(timeHHMM, now);
  if (!next) {
    return null;
  }
  return next.getTime() - now.getTime();
}

/** Suggested time options for the picker (every 30 min). */
export function reminderTimeOptions(): string[] {
  const options: string[] = [];
  const PAD = 2;
  const HALF_HOUR_STEPS = 2;
  for (let h = 0; h < HOURS_PER_DAY; h += 1) {
    for (let s = 0; s < HALF_HOUR_STEPS; s += 1) {
      const m = s * (MINUTES_PER_HOUR / HALF_HOUR_STEPS);
      options.push(`${String(h).padStart(PAD, "0")}:${String(m).padStart(PAD, "0")}`);
    }
  }
  return options;
}
